import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FollowUpDialog = ({ app, token, onClose, onUpdated }) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/applications/${app.id}/draft-followup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraft(res.data);
    } catch {
      toast.error("Could not generate a draft right now");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!app) return;
    if (app.draft_followup) {
      setDraft(app.draft_followup);
    } else {
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id]);

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/applications/${app.id}/send-followup`,
        { subject: draft.subject, body: draft.body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Follow-up sent to ${app.email}`);
      onUpdated(res.data);
      onClose();
    } catch {
      toast.error("Could not send the follow-up");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!app} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="followup-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif tracking-tight">
            Follow-up for {app?.hotel_name}
          </DialogTitle>
          <DialogDescription>
            Drafted by Gemini. Review and edit — sending emails it to {app?.email} and marks the
            application as contacted.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-3 py-14 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Gemini is drafting…</span>
          </div>
        ) : draft ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="followup-subject">Subject</Label>
              <Input
                id="followup-subject"
                data-testid="followup-subject-input"
                value={draft.subject}
                onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="followup-body">Body</Label>
              <textarea
                id="followup-body"
                data-testid="followup-body-input"
                rows={9}
                value={draft.body}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button
                data-testid="followup-regenerate-btn"
                variant="outline"
                onClick={generate}
                className="rounded-full"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Regenerate
              </Button>
              <Button
                data-testid="followup-send-btn"
                onClick={send}
                disabled={sending || !draft.subject.trim() || draft.body.trim().length < 10}
                className="rounded-full bg-accent text-accent-foreground font-semibold hover:bg-blue-700 transition-colors"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" /> Send email
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Sparkles className="mx-auto mb-3 h-6 w-6" />
            No draft available. Try regenerating.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
